import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { f2 } from '../../copyFlow2'
import { retryFailed, useBrowsePlan } from '../../lib/browse'
import { useSavedCount } from '../../lib/saved'
import { InlineMd } from '../Markdown'
import { v3 } from '../../copyV3'
import { buttonClass } from '../../ui/styles'
import { FADE, spring } from '../../ui/motion'

const UNDO_MS = 6000
const FIRST_MS = 10000 // the first save's bar also carries the consent line: time to read it

/**
 * Plan v3 (R-73): the Saved bar. It shows for a few seconds after each Save ("6 things saved" + See Saved), above the
 * tab bar, never on Saved itself. Publishes its height as --tray-h so the page can leave room for it while it shows.
 * B-120: on the person's first save the consent line shows here, in the fixed bar, not under the card's Save: a line
 * inserted in the card moved every card in its row and the next tap landed on Not for me.
 */
export function BrowseTray() {
  const s = useBrowsePlan()
  const count = useSavedCount()
  const navigate = useNavigate()
  const reduce = useReducedMotion() ?? false
  const ref = useRef<HTMLDivElement>(null)
  const [now, setNow] = useState(() => Date.now())
  const recent = s.lastAdded && now - s.lastAdded.at < (s.lastAdded.first ? FIRST_MS : UNDO_MS) ? s.lastAdded : null
  useEffect(() => {
    if (!s.lastAdded) return
    setNow(Date.now())
    const id = window.setTimeout(() => setNow(Date.now()), (s.lastAdded.first ? FIRST_MS : UNDO_MS) + 50)
    return () => window.clearTimeout(id)
  }, [s.lastAdded])

  // never on Saved itself (blind test r1: "See Saved" while already on Saved)
  const onSaved = useLocation().pathname === '/saved'
  const show = (Boolean(recent) || s.failed) && !onSaved

  useLayoutEffect(() => {
    const el = ref.current
    const root = document.documentElement
    if (!el || !show) {
      root.style.removeProperty('--tray-h')
      return
    }
    const publish = () => root.style.setProperty('--tray-h', `${Math.ceil(el.getBoundingClientRect().height)}px`)
    publish()
    const ro = new ResizeObserver(publish)
    ro.observe(el)
    return () => {
      ro.disconnect()
      root.style.removeProperty('--tray-h')
    }
  }, [show])

  const n = Math.max(1, count ?? s.count ?? 1)
  const line = s.failed ? v3('v3.save_error') : n === 1 ? v3('v3.toast_one', { count: n }) : v3('v3.toast_other', { count: n })

  return (
    <AnimatePresence>
      {show && (
        <motion.div ref={ref} key="tray" data-review-id="saved-bar" role="region" aria-label={v3('v3.nav_saved')}
          initial={reduce ? false : { y: 80 }} animate={{ y: 0 }} exit={reduce ? undefined : { y: 80 }} /* slides, never fades: a half-faded bar read as see-through (blind test r2) */
          transition={reduce ? FADE.enter : spring('layout')}
          className="fixed inset-x-0 bottom-[calc(56px+env(safe-area-inset-bottom))] z-40 border-t border-line bg-surface px-5 py-3 lg:inset-x-auto lg:right-8 lg:bottom-8 lg:w-[400px] lg:rounded-[20px] lg:p-4 lg:shadow-floating lg:ring-1 lg:ring-line">
          <div className="mx-auto w-full max-w-md md:max-w-[640px] lg:max-w-none">
            <div className="flex min-h-[52px] w-full items-center gap-3">
              <p aria-live="polite" className={`t-control min-w-0 flex-1 ${s.failed ? 'text-error' : 'text-ink'}`}>{line}</p>
              {s.failed ? (
                <button type="button" onClick={retryFailed} className={buttonClass('secondary', 'md')}>{f2('tray.retry')}</button>
              ) : (
                <button type="button" onClick={() => navigate('/saved')} className={buttonClass('primary', 'md', 'min-h-[52px] px-5')}>{v3('v3.toast_see')}</button>
              )}
            </div>
            {recent?.first && !s.failed && <ConsentLine force className="mt-2" />}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

/** Room at the end of a page so the last card clears the tray. */
export function TraySpacer() {
  return <div aria-hidden="true" style={{ height: 'calc(var(--tray-h, 0px) + 32px)' }} />
}

/**
 * The consent line, shown with the person's first Save, under that button, once (blind tests r1-r3). Wording: Astra
 * round 3 (v3.consent); Terms and Privacy stay links.
 */
export function ConsentLine({ className = '', force = false }: { className?: string; force?: boolean }) {
  const s = useBrowsePlan()
  const saved = useSavedCount()
  // force: the card whose Save tap was the person's first keeps showing it under that button (blind test r3)
  if (!force && (s.consentShown || (saved ?? 0) > 0 || Object.values(s.added).some((a) => a.status !== 'failed'))) return null
  const text = v3('v3.consent').replace(/\bTerms\b/, '[Terms](/terms)').replace(/\bPrivacy\b/, '[Privacy](/privacy)')
  return <p data-review-id="consent-line" className={`t-caption text-ink-2 [&_a]:underline ${className}`}><InlineMd text={text} /></p>
}
